'use client';

import { portalApi } from '@/lib/http/axios';

import type {
    CreditActivityPage,
    CreditEarlierActivity,
    CreditHolds,
    CreditUsage,
} from './types/activity';
import type { CreditBalance } from './types/balance';
import {
    ADJUSTMENTS_CSV_LIMIT,
    LEDGER_READ_ENDPOINTS,
    type CreditActivityQuery,
    type CreditAdjustmentsCsvQuery,
    type CreditEarlierQuery,
    type CreditFleetBalances,
    type CreditFleetQuery,
    type CreditRatioChange,
    type CreditReadTarget,
    type CreditUsageQuery,
} from './types/ledger-reads';

/** Drops empty values, so a query sends only what was set (the API refuses unknown fields). */
export function cleanParams(params: object): Record<string, string | number | boolean> {
    const out: Record<string, string | number | boolean> = {};
    for (const [key, value] of Object.entries(params)) {
        if (value === undefined || value === null || value === '') continue;
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
            out[key] = value;
        }
    }
    return out;
}

/** The client or staff URL for a read, and `view=client` where the target asks for it. */
function routeOf(
    target: CreditReadTarget,
    own: string,
    org: (orgId: string) => string,
): { url: string; view: Record<string, string> } {
    if (target.kind === 'own') return { url: own, view: {} };
    return { url: org(target.orgId), view: target.view ? { view: target.view } : {} };
}

/**
 * Reads on the new balance (M4S-5895, plan step 24; docs/pdlc/credits-ui.md §2.4, §7). Read-only:
 * nothing here writes. Each read takes a `CreditReadTarget`, so one call site serves the client
 * (`own`), View-as (`org` with `view: 'client'`) and the CS page (`org`, internal).
 */
class CreditLedgerService {
    /** The caller's own org balance (no names). */
    async getBalance(): Promise<CreditBalance> {
        const { data } = await portalApi.get<CreditBalance>(LEDGER_READ_ENDPOINTS.BALANCE);
        return data;
    }

    /** One org's balance (staff). The balance carries no names, so it has no `view`. */
    async getOrgBalance(orgId: string): Promise<CreditBalance> {
        const { data } = await portalApi.get<CreditBalance>(
            LEDGER_READ_ENDPOINTS.ORG_BALANCE(orgId),
        );
        return data;
    }

    /** The balance for a target: the client endpoint for `own`, the staff one for an org. */
    async getBalanceFor(target: CreditReadTarget): Promise<CreditBalance> {
        return target.kind === 'own' ? this.getBalance() : this.getOrgBalance(target.orgId);
    }

    /** Every org, flipped or not, filtered on the server (staff). */
    async getFleetBalances(query: CreditFleetQuery = {}): Promise<CreditFleetBalances> {
        const { data } = await portalApi.get<CreditFleetBalances>(
            LEDGER_READ_ENDPOINTS.FLEET_BALANCES,
            { params: cleanParams(query) },
        );
        return data;
    }

    /** The Rate Card's "DANI conversion changes": every ratio change, newest first (staff). */
    async getRatioChanges(): Promise<CreditRatioChange[]> {
        const { data } = await portalApi.get<CreditRatioChange[]>(
            LEDGER_READ_ENDPOINTS.RATIO_CHANGES,
        );
        return data;
    }

    /** The fixed CS reason codes, for the fleet's reason-code filter (staff). */
    async getFleetReasonCodes(): Promise<{ code: string; label: string; clientLabel: string }[]> {
        const { data } = await portalApi.get<
            { code: string; label: string; clientLabel: string }[]
        >(LEDGER_READ_ENDPOINTS.FLEET_REASON_CODES);
        return data;
    }

    /**
     * "Export adjustments (CSV)": every CS row in the period with org, reason code, credits, actor
     * and related row, built by the API (staff only: it carries the internal reason).
     */
    async downloadAdjustmentsCsv(query: CreditAdjustmentsCsvQuery): Promise<Blob> {
        const { data } = await portalApi.get<Blob>(LEDGER_READ_ENDPOINTS.ADJUSTMENTS_CSV, {
            params: { ...cleanParams(query), limit: ADJUSTMENTS_CSV_LIMIT },
            responseType: 'blob',
        });
        return data;
    }

    async getUsage(target: CreditReadTarget, query: CreditUsageQuery = {}): Promise<CreditUsage> {
        const { url, view } = routeOf(
            target,
            LEDGER_READ_ENDPOINTS.USAGE,
            LEDGER_READ_ENDPOINTS.ORG_USAGE,
        );
        const { data } = await portalApi.get<CreditUsage>(url, {
            params: { ...cleanParams(query), ...view },
        });
        return data;
    }

    async getActivity(
        target: CreditReadTarget,
        query: CreditActivityQuery = {},
    ): Promise<CreditActivityPage> {
        const { url, view } = routeOf(
            target,
            LEDGER_READ_ENDPOINTS.ACTIVITY,
            LEDGER_READ_ENDPOINTS.ORG_ACTIVITY,
        );
        const { data } = await portalApi.get<CreditActivityPage>(url, {
            params: { ...cleanParams(query), ...view },
        });
        return data;
    }

    /** "Earlier activity (before {T0})": today's list cut at T0, scoped the same way. */
    async getEarlierActivity(
        target: CreditReadTarget,
        query: CreditEarlierQuery = {},
    ): Promise<CreditEarlierActivity> {
        const { url, view } = routeOf(
            target,
            LEDGER_READ_ENDPOINTS.ACTIVITY_EARLIER,
            LEDGER_READ_ENDPOINTS.ORG_ACTIVITY_EARLIER,
        );
        const { data } = await portalApi.get<CreditEarlierActivity>(url, {
            params: { ...cleanParams(query), ...view },
        });
        return data;
    }

    async getHolds(target: CreditReadTarget): Promise<CreditHolds> {
        const { url, view } = routeOf(
            target,
            LEDGER_READ_ENDPOINTS.HOLDS,
            LEDGER_READ_ENDPOINTS.ORG_HOLDS,
        );
        const { data } = await portalApi.get<CreditHolds>(url, { params: view });
        return data;
    }

    /**
     * "Download activity (CSV)": the server builds it, D6-scoped, with the rows from before T0 in a
     * second section. For an org without `view` it is the internal copy (actor, internal reason,
     * counterparty, dollars at each row's date); with `view: 'client'`, exactly the client's own.
     */
    async downloadActivityCsv(
        target: CreditReadTarget,
        query: CreditActivityQuery = {},
    ): Promise<Blob> {
        const { url, view } = routeOf(
            target,
            LEDGER_READ_ENDPOINTS.ACTIVITY_EXPORT,
            LEDGER_READ_ENDPOINTS.ORG_ACTIVITY_EXPORT,
        );
        const { data } = await portalApi.get<Blob>(url, {
            params: { ...cleanParams(query), ...view },
            responseType: 'blob',
        });
        return data;
    }
}

export const creditLedgerService = new CreditLedgerService();

/** The file name the browser saves an activity export under. */
export function activityCsvFileName(target: CreditReadTarget, today: Date = new Date()): string {
    const day = today.toISOString().slice(0, 10);
    const who =
        target.kind === 'own'
            ? 'credits-activity'
            : `credits-activity-${target.orgId}${target.view === 'client' ? '-client' : ''}`;
    return `${who}-${day}.csv`;
}

/** Hands a downloaded file to the browser to save. */
export function saveBlob(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
}
